import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  Output,
  output,
  OutputEmitterRef,
  Signal,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import {
  ISideNavContentCategory,
  isSideNavContentItemComponent,
  TSideNavContentItem,
} from './side-nav-content-category.interface';
import {NgForOf, NgIf} from '@angular/common';
import {AuthorizeVisibilityDirective, EsvgFiles, IControlObject} from 'frontier/nucleus';
import {MatIcon} from '@angular/material/icon';
import {SectionTitleComponent} from './section-title/section-title.component';
import {MatDivider} from '@angular/material/divider';
import {MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {Observable, Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {AUTHORIZATION_CONFIG_TOKEN} from 'frontier/nucleus/src/lib/authorization/control-list.token';
import {HelpIconComponent} from 'frontier/browserkit/src/lib/components/help-icon/help-icon.component';
import {MatTooltip} from '@angular/material/tooltip';
import {UiHelperService} from 'frontier/browserkit/src/lib/components/ui-helper/ui-helper.service';
import {MatIconButton} from '@angular/material/button';

type TSideDrawerState = 'opened' | 'closed';

@Component({
  selector: 'lib-side-nav-content',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    MatIcon,
    SectionTitleComponent,
    MatDivider,
    MatMenuTrigger,
    MatMenu,
    MatMenuContent,
    MatMenuItem,
    AuthorizeVisibilityDirective,
    HelpIconComponent,
    MatTooltip,
    MatIconButton,
  ],
  templateUrl: './side-nav-content.component.html',
  styleUrl: './side-nav-content.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class SideNavContentComponent {
  protected readonly AUTHORIZATION_CONFIG = inject(AUTHORIZATION_CONFIG_TOKEN, {optional: true});

  private _uiHelperService: UiHelperService = inject(UiHelperService)

  private _categoryVisibilityCache: Map<ISideNavContentCategory, boolean | string[]>
    = new Map<ISideNavContentCategory, string[] | boolean>();

  matMenuTrigger: Signal<readonly MatMenuTrigger[]> = viewChildren<MatMenuTrigger>(MatMenuTrigger);
  sidePanelCategories: InputSignal<ISideNavContentCategory[]> = input.required<ISideNavContentCategory[]>();
  state: ModelSignal<TSideDrawerState> = model.required<TSideDrawerState>();
  showHelpIcons: InputSignal<boolean> = input<boolean | null>(false);
  itemClicked: OutputEmitterRef<TSideNavContentItem> = output<TSideNavContentItem>();

  protected readonly EsvgFiles = EsvgFiles;

  private mouseX: number = 0;
  private mouseY: number = 0;

  @Output() openHelp: EventEmitter<string> = new EventEmitter<string>();

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    this.mouseX = event.clientX;
    this.mouseY = event.clientY;
  }

  mouseLeave$: Subject<number> = new Subject<number>();
  mouseEnter$: Subject<number> = new Subject<number>();
  waitingToClose$: Observable<number> = this.mouseLeave$.pipe(
    debounceTime(500),
    takeUntil(this.mouseEnter$),
  );

  getMousePosition(): { x: number, y: number } {
    return {x: this.mouseX, y: this.mouseY};
  }

  onToggleOpened(): void {
    this.state.update((state: TSideDrawerState): TSideDrawerState => state === 'closed' ? 'opened' : 'closed');
  }

  onMenuTriggerHovered(i: number): void {
    console.log('hovered', i);
    this.mouseEnter$.next(i);
    this.waitingToClose$.subscribe((res: number) => {
      const relatedTarget: HTMLElement = document.elementFromPoint(this.getMousePosition().x, this.getMousePosition().y) as HTMLElement;
      if (
        !(relatedTarget?.classList.contains('mat-drawer') ||
          relatedTarget?.classList.contains('mat-mdc-menu-item') ||
          relatedTarget?.classList.contains('side-nav-menu') ||
          relatedTarget?.classList.contains('hover-menu-span'))
      ) {
        this.matMenuTrigger().at(res).closeMenu()
      }
    });
    this.matMenuTrigger().forEach((menu: MatMenuTrigger, index: number) => {
      if (index !== i) menu.closeMenu();
    })
    this.matMenuTrigger()?.at(i)?.openMenu();
  }

  onMenuMouseLeaved(i: number, evt: MouseEvent): void {
    let relatedTarget: HTMLElement = evt.relatedTarget as HTMLElement;
    if (
      relatedTarget?.classList.contains('mat-drawer') ||
      relatedTarget?.classList.contains('mat-mdc-menu-item') ||
      relatedTarget?.classList.contains('hover-menu-span')
    ) {
      return
    }
    this.mouseLeave$.next(i);
  }

  onMenuMouseLeavedFromMenu(i: number, evt: MouseEvent): void {
    this.matMenuTrigger()?.at(i)?.closeMenu();
  }

  // This function is used to check if the user has the necessary permissions to see the item
  // If the item is a component, it will check the guids in the AUTHORIZATION_CONFIG
  // If the item is a link, everyone can see it
  getAuthorizeVisibilityGuids(item: TSideNavContentItem): string[] | true {
    if (!this.AUTHORIZATION_CONFIG) return true;

    if (isSideNavContentItemComponent(item)) {
      const guids: string[] = this.AUTHORIZATION_CONFIG[item.componentName];
      if (!guids?.length) {
        // If there are no guids, it means that everyone can see it
        return true;
      }
      return guids
    }
    // If it is a link, everyone can see it
    return true;
  }

  getAuthorizeVisibilityCategory(category: ISideNavContentCategory): string[] | boolean {
    // To prevent infinite loops by triggering and getting triggered by change detection, calculate visibility only once
    // for each category and store the result in a cache.
    if (this._categoryVisibilityCache.has(category)) {
      return this._categoryVisibilityCache.get(category)!;
    }

    const result: boolean | string[] = this._calculateAuthorizeVisibilityCategory(category);
    this._categoryVisibilityCache.set(category, result);
    return result;
  }

  /**
   * Determines if the category has any visible items to adjust its own visibility accordingly.
   * If the category has no items, no one can see the category.
   * If the AUTHORIZATION_CONFIG is not defined, everyone can see the category.
   * If the category has any item that everybody can see, everybody can see the category.
   * Else accumulate the guids of the items that permit the user to see the item and therefore its category.
   * @param category
   * @private
   */
  private _calculateAuthorizeVisibilityCategory(category: ISideNavContentCategory): string[] | boolean {
    if (category.items.length === 0) return false;
    if (!this.AUTHORIZATION_CONFIG) return true;

    const mappedItems: (string[] | true)[]
      = category.items.map((item: TSideNavContentItem): string[] | true => this.getAuthorizeVisibilityGuids(item));

    if (mappedItems.some((item: string[] | true): boolean => item === true)) {
      return true;
    }

    return (mappedItems as string[][]).flat();
  }

  showHelpForSideNav(): void {
    this.openHelp.emit('seitennavigation.html#' + (this.state() == 'opened' ? 'aus' : 'ein') + 'geklappter-modus');
  }

  showHelpForItem(item: TSideNavContentItem): void {
    if (!item.helpComponent) {
      console.warn('No extended help found for item', item);
      return;
    }

    this._uiHelperService.openHelp({component: item.helpComponent});
  }

  getAlertTooltip(alert: IControlObject[]): string {
    return 'Folgende Elemente bedürfen Ihrer Aufmerksamkeit: ' +
      alert.map((a: IControlObject): string => a.name).join(', ');
  }
}
